import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FileManagerService } from "@services/file-manager/file-manager.service";
import { FileUploadOptions, FileUploadType } from "@shared/constants/file-manager/file-upload-options";

import { FileManagerUpload } from "@shared/models/file-manager/file-manager-upload.model";
import { take } from "rxjs/operators";
import { filter, Subscription } from "rxjs";
import { FileUploadStatus } from "@shared/constants/file-manager/file-upload-status";
import { getFileExtension } from "@shared/models/file-manager/file-manager-doc";

import { PreviewImageUpload } from "./prompt-input-preview-images";

@Component({
  selector: 'prompt-input-preview-images',
  templateUrl: './prompt-input-preview-images.component.html',
  styleUrls: ['./prompt-input-preview-images.component.css']
})
export class PromptInputPreviewImagesComponent implements OnChanges, OnDestroy {
  @Input() pastedImage?: File;

  previewImages: PreviewImageUpload[] = [];
  uploadedFiles: FileManagerUpload[] = [];
  fileType: FileUploadType = FileUploadOptions['IMAGE'];
  maxImageCount = 5;

  private uploadedFilesSubscription?: Subscription;

  constructor(
    private fileManagerService: FileManagerService
  ) {
    this.setupUploadedFilesSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pastedImage'] && this.pastedImage) {
      const currentFileType = this.fileManagerService.selectedFileType$.value;
      if (currentFileType !== this.fileType) {
        this.fileManagerService.cleanSelectedAndSetFileTypeToImage();
      }
      this.fileManagerService.getTotalUploadsCount().pipe(
        take(1)
      ).subscribe(totalCount => {
        if (totalCount < this.maxImageCount) {
          this.uploadPastedImage(this.pastedImage!);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.uploadedFilesSubscription?.unsubscribe();
    this.fileManagerService.cleanUploadedFiles();
  }

  setupUploadedFilesSubscription() {
    this.uploadedFilesSubscription = this.fileManagerService.uploadedFiles$
      .pipe(
        filter(files => files.length > 0
          && files.every(file => {
            const fileExtension = getFileExtension(file.file.name);
            return file.id !== undefined
              && fileExtension !== null
              && this.fileType.extensions.includes(fileExtension);
          })
        )
      )
      .subscribe((files: FileManagerUpload[]) => {
        files.forEach((file) => {
          this.fileManagerService.convertUploadToFileManagerDoc(file);
        });
        this.fileManagerService.confirmSelectedFiles();
      });
  }

  uploadPastedImage(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const imagePreview: PreviewImageUpload = {
        dataUrl: e.target.result,
        status: 'uploading'
      };
      this.previewImages.push(imagePreview);
      const currentIndex = this.previewImages.length - 1;
      this.fileManagerService.incrementUploadsInProgress();
      this.uploadImageFileUpload(file, imagePreview, currentIndex);
    };
    reader.readAsDataURL(file);
  }

  private uploadImageFileUpload(file: File, imagePreview: PreviewImageUpload, currentIndex: number) {
    this.fileManagerService.uploadFiles([file], this.fileType).forEach((fileUpload) => {
      if (!fileUpload.upload) return;
      fileUpload.upload
        .pipe(
          filter(status => status.status === FileUploadStatus.COMPLETED),
          take(1)
        )
        .subscribe({
          next: (completedStatus) => {
            const completedUpload: FileManagerUpload = {
              ...fileUpload,
              ...completedStatus
            };

            if (completedUpload.id) {
              this.fileManagerService.addUploadedFile({ ...completedUpload });
              imagePreview.status = 'completed';
            } else {
              this.previewImages.splice(currentIndex, 1);
            }
            this.fileManagerService.decrementUploadsInProgress();
          },
          error: () => {
            this.previewImages.splice(currentIndex, 1);
            this.fileManagerService.decrementUploadsInProgress();
          }
        });
    });
  }
}
